/**
 * The cave generator (docs/spec/03 §4): fresh caves for the endless Expedition and the Daily Dig.
 * Pure and deterministic: the same arguments always give the same cave.
 */

import { mixSeed } from '../../sim/rng';
import type { CaveDef } from '../types';
import { clampDepth, generateReport } from './generate';

export interface GenerateOptions {
  /** Allow the Deep elements (x s 1-6 t). */
  deep?: boolean;
}

/** The Expedition turns the Deep elements on from this depth. */
export const DEEP_FROM = 8;
/** Every this-many-th Expedition depth is a 20 x 12 bonus cave. */
export const BONUS_EVERY = 5;
/** The Daily Dig's difficulty. */
export const DAILY_DEPTH = 6;

/** A fresh 40 x 22 cave. depth >= 1 sets difficulty; same (seed, depth, options) = same cave. One level entry. */
export function generateCave(seed: number, depth: number, options: GenerateOptions = {}): CaveDef {
  const d = clampDepth(depth);
  return generateReport({ seed, depth: d, deep: options.deep === true, bonus: false, id: `G${d}` }).def;
}

/**
 * The endless Expedition: cave `depth` of the run seeded `runSeed`. Deep elements unlock from
 * depth 8. Every fifth depth (5, 10, 15 ...) is a 20 x 12 bonus cave with `intermission: true`.
 */
export function expeditionCave(runSeed: number, depth: number): CaveDef {
  const d = clampDepth(depth);
  return generateReport({
    seed: mixSeed(runSeed, d),
    depth: d,
    deep: d >= DEEP_FROM,
    bonus: d % BONUS_EVERY === 0,
    id: `E${d}`,
  }).def;
}

/** Days since 1970-01-01 for a proleptic Gregorian date (Howard Hinnant's days_from_civil). */
function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const mp = (month + 9) % 12;
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

const DAY_ONE = daysFromCivil(2026, 1, 1);

/** Day number for a UTC date: 2026-01-01 is day 1. Pure: pass the numbers, never read the clock. */
export function dailyNumber(year: number, month: number, day: number): number {
  return daysFromCivil(year, month, day) - DAY_ONE + 1;
}

/** Today's Daily Dig cave (seed from the day number, depth 6, Classic elements only). */
export function dailyCave(year: number, month: number, day: number): CaveDef {
  const n = dailyNumber(year, month, day);
  return generateReport({ seed: n, depth: DAILY_DEPTH, deep: false, bonus: false, id: `D${n}` }).def;
}
